import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Paper,
  Container,
  IconButton,
  Box,
  Grid,
} from "@mui/material";
import {
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";

// Replace these imports with your actual Redux actions/slices
import { fetchProfilesByDescription } from "../../redux/profilesSlice";
import { fetchCurrentUser } from "../../redux/usersSlice";

import ProfileCard from "../Profiles/ProfileCard";
import ProfileDetails from "../Profiles/ProfileDetails";
import CustomLoader from "../Common/CustomLoader";

// Styles (you can keep or adapt your CSS files)
import "../Profiles/styles/ChatResults.css";
import "../Profiles/styles/ProfileList.css";

const AppelOffreResults = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // We read `desc` from the query string: "?desc=someEncodedStuff"
  const [searchParams] = useSearchParams();
  const description = searchParams.get("desc") || "";

  const { chatProfiles, loading, error, message } = useSelector(
    (state) => state.profiles
  );

  // Local state for pagination, etc.
  const [page, setPage] = useState(1);
  const profilesPerPage = 6;

  // Selected profile state
  const [selectedProfile, setSelectedProfile] = useState(null);

  // Add ref for scroll-to-top
  const containerRef = React.useRef(null);

  // Reset to page 1 when the description changes
  useEffect(() => {
    setPage(1);
  }, [description]);

  // On mount, fetch user & fetch profiles by desc
  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/interdit");
        return;
      }

      const userAction = await dispatch(fetchCurrentUser());
      const user = userAction.payload;

      if (!user || user.roles?.includes("ROLE_USER")) {
        navigate("/interdit");
        return;
      }

      if (description.trim()) {
        dispatch(
          fetchProfilesByDescription({
            description,
            numberOfProfiles: 10,
            page: 1,
            limit: 10,
          })
        );
      }
    };

    fetchData();
  }, [description, dispatch, navigate]);

  // Scroll to top helper
  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Derived data for pagination
  const indexOfLastProfile = page * profilesPerPage;
  const indexOfFirstProfile = indexOfLastProfile - profilesPerPage;
  const currentProfiles = chatProfiles.slice(
    indexOfFirstProfile,
    indexOfLastProfile
  );

  // Total pages
  const totalPages = Math.ceil(chatProfiles.length / profilesPerPage);

  return (
    <Box
      ref={containerRef}
      sx={{
        background: "linear-gradient(135deg, #ffffff, #d1d9e6)",
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "2rem",
      }}
    >
      <Container
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          borderRadius: "8px",
          boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
          padding: "2rem",
          width: "100%",
          maxWidth: "1200px",
        }}
      >
        {selectedProfile ? (
          <>
            <IconButton
              onClick={() => setSelectedProfile(null)}
              sx={{
                color: "#2F4E8B",
                marginBottom: "1rem",
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <ProfileDetails
              profile={selectedProfile}
              onUpdated={() => setSelectedProfile(null)}
            />
          </>
        ) : (
          <Grid container spacing={4}>
            <Grid item xs={12}>
              {/* Description block */}
              <Paper
                elevation={3}
                sx={{
                  padding: "1.5rem",
                  backgroundColor: "#f5f5f5",
                  marginBottom: "2rem",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ textAlign: "center", color: "#2F4E8B", fontWeight: "bold" }}
                >
                  Résultat des candidats de l'Appel d'Offre:
                </Typography>
              </Paper>

              {/* Loading */}
              {loading && (
                <div className="loading-container">
                  <CustomLoader />
                  <Typography
                    variant="body1"
                    style={{
                      marginTop: "1rem",
                      fontWeight: "bold",
                      fontSize: "1.2rem",
                      color: "#27437f",
                    }}
                  >
                    Recherche des profils en cours... Merci de patienter ⏰
                  </Typography>
                </div>
              )}

              {/* Error */}
              {!loading && error && (
                <Typography
                  variant="body1"
                  color="error"
                  style={{ marginTop: "1rem" }}
                >
                  {error}
                </Typography>
              )}

              {/* Profiles List */}
              {!loading && !error && currentProfiles.length > 0 && (
                <>
                  <Grid container spacing={2} sx={{ justifyContent: "center" }}>
                    {currentProfiles.map((profile) => (
                      <Grid item xs={12} sm={6} md={4} key={profile.id}>
                        <ProfileCard
                          profile={profile}
                          onClick={() => setSelectedProfile(profile)}
                        />
                      </Grid>
                    ))}
                  </Grid>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      mt={4}
                      gap={2}
                    >
                      <IconButton
                        onClick={() => {
                          setPage(page - 1);
                          setTimeout(scrollToTop, 0);
                        }}
                        disabled={page === 1}
                        sx={{ color: "#2F4E8B" }}
                      >
                        <ArrowBackIosIcon />
                      </IconButton>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          backgroundColor: "#2F4E8B",
                          color: "#fff",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          fontWeight: "bold",
                        }}
                      >
                        {page}
                      </Box>
                      <IconButton
                        onClick={() => {
                          setPage(page + 1);
                          setTimeout(scrollToTop, 0);
                        }}
                        disabled={page === totalPages}
                        sx={{ color: "#2F4E8B" }}
                      >
                        <ArrowForwardIosIcon />
                      </IconButton>
                    </Box>
                  )}
                </>
              )}

              {/* If there's some message */}
              {!loading && message && (
                <Typography
                  variant="body1"
                  color="textSecondary"
                  style={{ marginTop: "1rem" }}
                >
                  {message}
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default AppelOffreResults;
